import { AuthRequest, Method } from 'src/util/Request';
import { baseUrl, genericNetworkErrorHandler } from '.';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { DeliveryManagementFilters, DeliveryManagementMapping } from '@lanterds/types';
import { Dataset } from 'src/components/Views/DeliveryManagement/types';

enum UrlTypes {
    MissingStops = '/deliveryManagement/missingStops/search',
    LateArrivals = '/deliveryManagement/lateArrivals/search',
}

export const getDeliveryData = (dataSet: Dataset, filters?: Partial<DeliveryManagementFilters>, isEnabled = false) => {
    const headers = {
        'Content-Type': 'application/json',
    };

    let uri = '';

    switch (dataSet) {
        case Dataset.MissingStops:
            uri = UrlTypes.MissingStops;
            break;
        case Dataset.LateArrivals:
            uri = UrlTypes.LateArrivals;
            break;
        default:
            break;
    }

    const results = useInfiniteQuery({
        queryKey: [uri, filters, dataSet],
        queryFn: async ({ pageParam }) => {
            const body = {
                filters: {
                    ...filters,
                    startDate: filters?.startDate,
                    endDate: filters?.endDate,
                    showModified: filters?.showModified,
                },
                pageSize: filters?.pageSize,
                pageNumber: pageParam,
            };

            const res = await AuthRequest(`${baseUrl}${uri}`, Method.POST, headers, body);

            if (res.ok) {
                const parcels = await res.json();
                return parcels;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            const totalResults = lastPage.totalRecords;
            const currentResultsLength = lastPage.data.length;

            if (
                filters?.pageSize &&
                (currentResultsLength < filters.pageSize || allPages.length * filters.pageSize >= totalResults)
            ) {
                return undefined; // No more pages if we reach the total count
            }
            return lastPageParam + 1; // Move to the next page
        },
        throwOnError: true,
        enabled: isEnabled,
        // Allows previous query data to remain when query key is updated based on dependency of filters
        placeholderData: keepPreviousData,
    });
    return results;
};

export const saveMissingStops = () => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const uri = '/deliveryManagement/missingStops';

    const results = useMutation({
        mutationKey: [uri],
        mutationFn: async (updatedMissingStops: Partial<DeliveryManagementMapping>[]) => {
            const res = await AuthRequest(`${baseUrl}${uri}`, Method.PUT, headers, updatedMissingStops);

            if (res.ok) {
                const deliveries = await res.json();
                return deliveries;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};

export const saveLateArrivals = () => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const uri = '/deliveryManagement/lateArrivals';

    const results = useMutation({
        mutationKey: [uri],
        mutationFn: async (updatedLateArrivals: Partial<DeliveryManagementMapping>[]) => {
            const res = await AuthRequest(`${baseUrl}${uri}`, Method.PUT, headers, updatedLateArrivals);

            if (res.ok) {
                const deliveries = await res.json();
                return deliveries;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};

export const getFilters = (isEnabled = true) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    const uri = '/deliveryManagement/filters';
    const results = useQuery({
        queryKey: [uri],
        queryFn: async () => {
            const res = await AuthRequest(`${baseUrl}${uri}`, Method.GET, headers);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
        enabled: isEnabled,
    });
    return results;
};

export const getLateReasons = (isEnabled = true) => {
    const headers = {
        'Content-Type': 'application/json',
    };
    const uri = '/deliveryManagement/lateReasons';
    const results = useQuery({
        queryKey: [uri],
        queryFn: async () => {
            const res = await AuthRequest(`${baseUrl}${uri}`, Method.GET, headers);

            if (res.ok) {
                const details = await res.json();
                return details;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        refetchOnWindowFocus: false,
        throwOnError: true,
        enabled: isEnabled,
    });
    return results;
};

export const undoDeliveryRecord = () => {
    const headers = {
        'Content-Type': 'application/json',
    };

    const uri = '/deliveryManagement/record';

    const results = useMutation({
        mutationKey: [uri],
        mutationFn: async (keys: { keySource: string; keyValue: string }) => {
            const res = await AuthRequest(`${baseUrl}${uri}`, Method.POST, headers, keys);

            if (res.ok) {
                const deliveries = await res.json();
                return deliveries;
            } else {
                await genericNetworkErrorHandler(res);
            }
        },
        throwOnError: true,
    });
    return results;
};
